


import React, { useState } from "react";
import { useTranslation } from "src/context/Translations/context";
import Container from "src/components/Sortable/Container";
import { verticalListSortingStrategy } from "src/components/Sortable/Container/strategy";
import SortableItem from "src/components/Sortable/Item";
import { RCCButton, RCCDropDownItem, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import { standardErrorMessages } from "src/utils/errors";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import SortableHandle from "src/components/Sortable/Handle";
import DropdownWithBackground from "src/components/DropdownWithBackground";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { Card } from "./styles";
import { IMdtbb001, IRefDiagnosticModals } from "../../interface";

export interface ISidebarCards{
  items: IMdtbb001[];
	setItems: React.Dispatch<React.SetStateAction<IMdtbb001[]>>;
  refModalAddChangeDelete: React.RefObject<IRefDiagnosticModals>;
  onClickCardFuncion: (canvas: number) => void;
  selectedDiagnostic?: IMdtbb001;
}

const SidebarCanvas: React.FC<ISidebarCards> = ({
	items,
	setItems,
	refModalAddChangeDelete,
	onClickCardFuncion,
	selectedDiagnostic
}) => {

	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	
	const [tempItems, setTempItems] = useState<IMdtbb001[]>(items);

	const [activeId, setActiveId] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [waitingForResponse, setWaitingForResponse] = useState(false);

	const queryReorderCanvas = async (diagnostics: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "Diagnostics/UpdateOrder",
				body: {
					rows: diagnostics
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: translate("Diagnóstico movido com sucesso"),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setItems(tempItems);
			standardErrorMessages(err, translate("Erro ao atualizar diagnóstico"), addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	const disableOptions = (idX: string) => {
		if(isDragging){
			if(activeId === idX)
				return false;
			return true;
		}
		if(waitingForResponse)
			return true;
		return false;
	};

	useEffectSkipInitialRender(() => {
		setItems(items);
	}, [items]);

	return (
  	<>
  		<Container
				onDragStart={(activeIdX: string) => {
					setIsDragging(true);
					setActiveId(activeIdX);
					setTempItems(items);
				}}
				executeOnDragEndWhenActiveAndOverEquals
  			onDragEnd={(activeIdX, overId) => {
					setIsDragging(false);
					setActiveId(null);

					if(activeIdX === overId)
						return; 	

					const activeIndex = items.findIndex((checks) => checks.id === activeIdX);
					const overIndex = items.findIndex((checks) => checks.id === overId);

					const orderedItems = arrayMove(items, activeIndex, overIndex);

					setItems(orderedItems);

					const rowsOrdered: any[] = [];

					orderedItems.forEach((item: any, index) => {
						const row = {
							mdb001Id: item.mdb001Id,
							mdb001Ordem: index + 1
						};
						rowsOrdered.push(row);
					});

					queryReorderCanvas(rowsOrdered);

  			}}
  			items={items.map((line) => ({
  				id: `${line.mdb001Id}`
  			}))}
				strategy={verticalListSortingStrategy}
				modifiers={[restrictToVerticalAxis]}
  		>	
				{items.map((card) => (
					<SortableItem 
						key={card.mdb001Id}
						id={`${card.mdb001Id}`}
						
					>
						<Card 
							key={card.mdb001Id} 														
							selected={card.mdb001Id === selectedDiagnostic?.mdb001Id}
							color="var(--green)"
							disableOptions={disableOptions(card.id)}
							onClick={(e) => {e.stopPropagation(); onClickCardFuncion(card.mdb001Id);}}
						>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<div className="card-head">
									<span className="icon-options">
										<div className="card-model-options-move">					
											<SortableHandle>
												<RCCTooltip 
													color="purple"
													text={translate("Mover Dianóstico")}
												>
													<RCCButton
														color="transparent"
														icon={{
															name: "arrows",
															color: card.mdb001Id === selectedDiagnostic?.mdb001Id ? "#FFF" : "var(--grey)"
														}}
														layout="isRounded"
														onClick={(e) => e.stopPropagation()}
													/>
												</RCCTooltip>	
											</SortableHandle>
										</div>
										<DropdownWithBackground
											position="left"
											useStopPropagation
											callerComponent={
												<RCCButton
													color="transparent"
												>
													<RCCIcon name="dotsVertical" size={18} color={card.mdb001Id === selectedDiagnostic?.mdb001Id ? "#FFF" : "var(--grey)"}/>
												</RCCButton>
											}
										>
											<RCCDropDownItem
												selfEnclose
												onClick={(e) => {e.stopPropagation(); refModalAddChangeDelete.current?.actionButtonChange(card.mdb001Id); } }
											>
												<RCCIcon name="pencil" />
												{translate("Alterar")}
											</RCCDropDownItem>
											<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
												<RCCDropDownItem
													selfEnclose
													onClick={(e) => {e.stopPropagation(); refModalAddChangeDelete.current?.actionButtonDelete(card.mdb001Id, card.mdb001Descricao);} }
												>
													<RCCIcon name="trash" />
													{translate("Excluir")}
												</RCCDropDownItem>						
											</CheckPermission>
										</DropdownWithBackground>
									</span>
								</div>											
							</CheckPermission>
						
	
							<div className="card-body"><span title={card.mdb001Descricao}>{card.mdb001Descricao}</span></div>
							<div className="card-footer"><span title={card.mda001Titulo}>{card.mda001Titulo}</span></div>
					
						</Card>
					</SortableItem>
				))}
  		</Container>

		</>
	);
};

export default SidebarCanvas;
